import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { FileText, Phone, CreditCard, MessageCircle, Play } from "lucide-react";

const PlacementSelectionProcess = ({ details }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  console.log("details : ", details);
  const steps = [
    {
      icon: FileText,
      title: "Fill Application Form",
      description: "Complete our detailed application form with your details",
      duration: "5 minutes",
    },
    {
      icon: Phone,
      title: "Selection Call",
      description:
        "You'll get a 1:1 call with Charchit to discuss your goals and fit",
      duration: "30 minutes",
    },
    {
      icon: CreditCard,
      title: "Secure Your Spot",
      description: `Make payment of ${(details?.price || 9999).toLocaleString(
        "en-IN"
      )} within 24 hours to confirm your place in the cohort`,
      duration: "2 minutes",
    },
    {
      icon: MessageCircle,
      title: "Join WhatsApp Group",
      description:
        "Get added to our exclusive cohort group for immediate access to resources and community",
      duration: "Instant",
    },
    {
      icon: Play,
      title: "Start Your Journey",
      description:
        "Begin your 60-day transformation with your first 1:1 session and personalized roadmap",
      duration: "Day 1",
    },
  ];

  return (
    <section id="process" className="py-24 relative overflow-hidden">
      {/* Background with dots + gradient overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "radial-gradient(#404040 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 20%, black 70%)",
        }}
      />

      {/* Glassmorphic background */}

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl  font-bold mb-6">
            <span className="bg-text-gradient bg-clip-text text-transparent">
              Selection Process
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Simple, transparent, and designed to find the right candidates
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical line - now visible on all devices */}
            <div className="absolute left-6 sm:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primaryTheme via-aurora-accent to-aurora-secondary" />

            {steps.map((step, index) => {
              const Icon = step.icon;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -50 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="relative flex items-start gap-4 sm:gap-8 mb-12 last:mb-0"
                >
                  {/* Step number and icon */}
                  <div className="flex flex-col items-center space-y-2 flex-shrink-0">
                    <div className="relative">
                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-primaryTheme/30 rounded-full blur-xl animate-pulse-glow" />

                      {/* Icon container */}
                      <div className="relative bg-card border-2 border-primaryTheme rounded-full p-3 sm:p-4 shadow-glass">
                        <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-primaryTheme" />
                      </div>
                    </div>

                    <div className="text-xs sm:text-sm font-medium text-primaryTheme bg-primaryTheme/10 px-2 sm:px-3 py-1 rounded-full">
                      Step {index + 1}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-glass-gradient backdrop-blur-glass border border-primaryTheme/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 group hover:border-primaryTheme/40 transition-all duration-300">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-lg sm:text-xl text-left font-semibold text-foreground group-hover:text-primaryTheme transition-colors">
                        {step.title}
                      </h3>
                      <span className="hidden md:inline text-sm text-primaryTheme bg-primaryTheme/10 px-3 py-1 rounded-full font-medium ">
                        {step.duration}
                      </span>
                    </div>

                    <p className="text-sm sm:text-base text-muted-foreground text-left leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Bottom note */}
        {/* Call-to-action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center  px-2 md:px-6 py-3 rounded-full bg-primaryTheme/10 border border-primaryTheme/20">
            <span className="text-primaryTheme font-medium">
              ✨ Cohort Strength: Handpicking 10 Students
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlacementSelectionProcess;
